import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { db } from '../offline/db';
import { deriveKey, decryptToken } from '../offline/crypto';

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE_URL = baseUrl.endsWith('/api/v1') ? baseUrl : `${baseUrl}/api/v1`;

const PWA_TOKEN_KEY = 'pwa_token';
const DEVICE_ID_KEY = 'device_id';

// ── Token Helper Functions ───────────────────────────────────────────────────────

/**
 * Retrieves the device ID from IndexedDB sync_meta, generating one if it doesn't exist.
 * The device ID is used in combination with user ID to derive the encryption key.
 */
export async function getDeviceId(): Promise<string> {
    try {
        const meta = await db.sync_meta.get(DEVICE_ID_KEY);
        if (meta && typeof meta.value === 'string') {
            return meta.value;
        }
    } catch {
        // If not found or error, generate a new one
    }

    // Generate a new device ID
    const deviceId = crypto.randomUUID();

    try {
        await db.sync_meta.put({
            key: DEVICE_ID_KEY,
            value: deviceId as unknown as number,
        });
    } catch {
        // If we can't save it, still return the generated ID for this session
    }

    return deviceId;
}

/**
 * Retrieves and decrypts the PWA token from IndexedDB.
 * Returns null if no token is stored or if decryption fails.
 */
export async function getPwaToken(): Promise<string | null> {
    try {
        const tokenData = await db.auth_tokens.get(PWA_TOKEN_KEY);

        if (!tokenData) {
            return null;
        }

        // Check if token is expired
        if (tokenData.expires_at < Date.now()) {
            // Token expired, delete it
            await db.auth_tokens.delete(PWA_TOKEN_KEY);
            return null;
        }

        // Get device ID for key derivation
        const deviceId = await getDeviceId();

        // Derive the encryption key from userId + deviceId
        const key = await deriveKey(tokenData.user_id, deviceId);

        // Decrypt the token
        const plainToken = await decryptToken(tokenData.iv, tokenData.ciphertext, key);

        return plainToken;
    } catch {
        // If anything fails, return null
        return null;
    }
}

/**
 * Clears the PWA token from IndexedDB.
 * Called on logout or on 401 response.
 */
export async function clearPwaToken(): Promise<void> {
    try {
        await db.auth_tokens.delete(PWA_TOKEN_KEY);
    } catch {
        // Ignore errors during deletion
    }
}

// ── API Client Class ─────────────────────────────────────────────────────────────

class ApiClient {
    private client: AxiosInstance;

    constructor() {
        this.client = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,  // send httpOnly auth_token cookie automatically
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });

        // Request interceptor — inject Bearer token from IndexedDB
        this.client.interceptors.request.use(
            async (config) => {
                // Skip auth token injection for auth endpoints themselves, and for the
                // 401-recovery retry (which deliberately wants cookie-only auth).
                if (config.url?.includes('/auth/') || (config as unknown as Record<string, unknown>)._retriedWithoutPwaToken) {
                    return config;
                }

                const token = await getPwaToken();

                if (token) {
                    // Don't overwrite an existing Authorization header
                    const headers = config.headers as Record<string, string> | undefined;
                    if (!headers?.Authorization) {
                        (config.headers as Record<string, string>) = {
                            ...(headers || {}),
                            Authorization: `Bearer ${token}`,
                        };
                    }
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor — on 401, retry once without the PWA bearer token before
        // giving up. A stale/invalid IndexedDB token otherwise permanently shadows a
        // freshly-set auth_token cookie (TokenFromCookie skips the cookie whenever a
        // Bearer header is already present), which would strand the user in a
        // login -> 401 -> redirected-to-login loop right after a successful login.
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const config = error.config;
                const hadBearerToken = !!(config?.headers as Record<string, string> | undefined)?.Authorization;

                // A 401 from the login endpoint is the server answering "wrong
                // credentials" — not an expired session. Redirecting here would tear the
                // page down before the login form could render the error, leaving the
                // user staring at a blank form with no idea what went wrong.
                const isLoginRequest = typeof config?.url === 'string' && /\/login\/?$/.test(config.url);

                // The retry exists to shake off a stale PWA token shadowing a fresh
                // cookie. The login endpoint never authenticates by token, so retrying
                // it only replays wrong credentials against the auth rate limiter.
                if (error.response?.status === 401 && config && hadBearerToken && !isLoginRequest && !config._retriedWithoutPwaToken) {
                    await clearPwaToken();
                    try {
                        const retryConfig = { ...config, _retriedWithoutPwaToken: true };
                        const headers = { ...(retryConfig.headers as Record<string, string> | undefined) };
                        delete headers.Authorization;
                        retryConfig.headers = headers;
                        return await this.client.request(retryConfig);
                    } catch (retryError) {
                        error = retryError;
                    }
                }

                if (error.response?.status === 401 && !isLoginRequest) {
                    await clearPwaToken();
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.get<T>(url, config);
    }

    public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post<T>(url, data, config);
    }

    public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.put<T>(url, data, config);
    }

    public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.patch<T>(url, data, config);
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.delete<T>(url, config);
    }
}

export const apiClient = new ApiClient();
export default apiClient;
